import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dna, ChevronDown, ChevronUp, Info } from "lucide-react";

/**
 * MolecularPathwayEducation — Phase 3 educational visualisation of four
 * major biological pathways that influence breast tissue homeostasis.
 *
 * NO PAM50 / tumour subtype language. Strictly educational.
 *
 * Each pathway exposes:
 *  - name + description
 *  - upstream lifestyle inputs (what activates it)
 *  - downstream tissue effects (educational)
 *  - current activation estimate (0-100)
 *  - modifiable levers with A/B/C evidence labels
 */

const BAND_COLORS = {
  low:      { bg: "bg-emerald-100 dark:bg-emerald-500/20", text: "text-emerald-700 dark:text-emerald-300", hex: "#22C55E" },
  moderate: { bg: "bg-amber-100 dark:bg-amber-500/20",     text: "text-amber-700 dark:text-amber-300",     hex: "#EAB308" },
  elevated: { bg: "bg-orange-100 dark:bg-orange-500/20",   text: "text-orange-700 dark:text-orange-300",   hex: "#F97316" },
  high:     { bg: "bg-rose-100 dark:bg-rose-500/20",       text: "text-rose-700 dark:text-rose-300",       hex: "#EF4444" },
};

const BAND_LABELS = {
  bg: { low: "Нисък", moderate: "Умерен", elevated: "Повишен", high: "Висок" },
  en: { low: "Low",   moderate: "Moderate", elevated: "Elevated", high: "High" },
  es: { low: "Bajo",  moderate: "Moderado", elevated: "Elevado", high: "Alto" },
};

const EVIDENCE_COLORS = {
  A: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  B: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  C: "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300",
};

const T = {
  bg: {
    title: "Молекулярни пътища (образователно)",
    intro: "Седем основни биологични сигнални пътя, които влияят върху средата на гръдната тъкан. Активационните стойности са образователни оценки от които може да променим фактори.",
    upstream: "Влияещи фактори",
    downstream: "Биологични ефекти",
    levers: "Които може да променим лоста",
    activation: "Активация",
  },
  en: {
    title: "Molecular Pathways (educational)",
    intro: "Seven major biological signalling pathways that influence the breast tissue environment. Activation values are educational estimates from modifiable factors.",
    upstream: "Influencing factors",
    downstream: "Biological effects",
    levers: "Modifiable levers",
    activation: "Activation",
  },
  es: {
    title: "Vías moleculares (educativo)",
    intro: "Siete principales vías de señalización biológica que influyen en el entorno del tejido mamario. Los valores de activación son estimaciones educativas de factores modificables.",
    upstream: "Factores influyentes",
    downstream: "Efectos biológicos",
    levers: "Palancas modificables",
    activation: "Activación",
  },
};

export default function MolecularPathwayEducation({ molecular, cardCls, isDark, lang = "bg" }) {
  const t = T[lang] || T.bg;
  const [openId, setOpenId] = useState(molecular?.pathways?.[0]?.id || null);
  const pathways = molecular?.pathways || [];
  const gated = !!molecular?.gated;
  const suff = molecular?.data_sufficiency || {};

  // iter698 — When data sufficiency is below threshold, show "Insufficient data"
  // instead of a potentially misleading activation score.
  if (gated) {
    const missingLabels = (suff.missing || []).slice(0, 6);
    return (
      <div className={`${cardCls} p-5`} data-testid="molecular-insufficient-data">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-semibold mb-1">
              {lang === "bg"
                ? "Недостатъчно данни за достоверна оценка на молекулярните пътища"
                : lang === "es"
                ? "Datos insuficientes para una evaluación fiable de las vías moleculares"
                : "Insufficient data for a reliable molecular-pathway estimate"}
            </div>
            <p className="text-xs opacity-70 mb-3">
              {lang === "bg"
                ? `Налични ${suff.score || 0}% от ключовите сигнали (минимум ${suff.min_required || 50}% са необходими). За да активирате тази секция, въведете липсващите данни в съответните близнаци.`
                : lang === "es"
                ? `${suff.score || 0}% de las señales clave disponibles (mínimo ${suff.min_required || 50}% requerido).`
                : `${suff.score || 0}% of key signals available (min ${suff.min_required || 50}% required).`}
            </p>
            {missingLabels.length > 0 && (
              <div className="space-y-1">
                <div className="text-[11px] font-semibold opacity-70 mb-1">
                  {lang === "bg" ? "Липсващи данни:" : lang === "es" ? "Datos faltantes:" : "Missing inputs:"}
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {missingLabels.map((m, i) => (
                    <li key={i} className="text-xs flex items-start gap-1.5">
                      <span className="text-amber-500 mt-0.5">•</span>
                      <span className="opacity-80">{m}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="molecular-pathway-list">
      <p className="text-xs opacity-60 mb-1">{t.intro}</p>

      {pathways.map((p) => {
        const open = openId === p.id;
        const band = BAND_COLORS[p.band] || BAND_COLORS.moderate;
        const langBands = BAND_LABELS[lang] || BAND_LABELS.bg;

        return (
          <div key={p.id} className={`${cardCls} overflow-hidden`} data-testid={`pathway-${p.id}`}>
            {/* Header */}
            <button
              onClick={() => setOpenId(open ? null : p.id)}
              className="w-full text-left p-4 flex items-center gap-3"
            >
              <Dna className="w-5 h-5" style={{ color: band.hex }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-base">{p.name_bg}</h3>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${band.bg} ${band.text}`}>
                    {langBands[p.band]} · {p.activation}/100
                  </span>
                </div>
                <p className="text-xs opacity-70 mt-0.5 line-clamp-1" title={p.description_bg}>{p.description_bg}</p>
              </div>
              {open ? <ChevronUp className="w-5 h-5 opacity-50" /> : <ChevronDown className="w-5 h-5 opacity-50" />}
            </button>

            {/* Activation bar */}
            <div className="px-4 pb-3">
              <div className="relative h-2 bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden">
                <motion.div
                  className="absolute top-0 bottom-0 left-0 rounded-full"
                  style={{ background: `linear-gradient(90deg, ${band.hex}88, ${band.hex})` }}
                  initial={{ width: 0 }}
                  animate={{ width: `${p.activation}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              </div>
            </div>

            {/* Body */}
            <AnimatePresence initial={false}>
              {open && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-4 pt-1 border-t border-pink-200/20 space-y-3">
                    <div>
                      <h4 className="text-xs font-bold opacity-70 mb-1">{t.upstream}</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {p.upstream_bg.map((u, i) => (
                          <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300">
                            {u}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* ── Estrogen metabolite mini-dashboard ───────── */}
                    {p.metabolites && (
                      <div className="rounded-xl p-3 bg-gradient-to-br from-pink-500/10 to-purple-500/10 border border-pink-200/30 dark:border-pink-500/20" data-testid={`pathway-${p.id}-metabolites`}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xs font-bold">{lang === "bg" ? "Естрогенови метаболити (образователно)" : lang === "es" ? "Metabolitos estrogénicos (educativo)" : "Estrogen metabolites (educational)"}</h4>
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            p.metabolites.ratio_label === "favourable" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" :
                            p.metabolites.ratio_label === "balanced"   ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300" :
                            "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300"
                          }`}>
                            2-OH / 16α-OH = {p.metabolites.ratio_2_16}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {Object.entries(p.metabolites.components).map(([key, c]) => {
                            const color = c.polarity === "protective" ? "#22C55E" :
                                          c.polarity === "unfavourable" ? "#EF4444" : "#EAB308";
                            return (
                              <div key={key} className="text-center" data-testid={`metab-${key}`}>
                                <div className="text-[9px] opacity-70 mb-0.5">{lang === "bg" ? c.label_bg : c.label_en}</div>
                                <div className="relative h-1.5 rounded-full bg-gray-200/40 dark:bg-white/10 overflow-hidden mb-0.5">
                                  <motion.div
                                    className="absolute top-0 bottom-0 left-0 rounded-full"
                                    style={{ background: color }}
                                    initial={{ width: 0 }}
                                    animate={{ width: `${c.pct}%` }}
                                    transition={{ duration: 0.9, ease: "easeOut" }}
                                  />
                                </div>
                                <div className="text-xs font-bold" style={{ color }}>{c.pct}%</div>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-[11px] mt-2 opacity-75 italic">{p.metabolites.interpretation_bg}</p>
                        {p.metabolites.data_source === "lifestyle_estimate" && (
                          <div className="mt-2 px-2 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/20 text-[10px] text-amber-800 dark:text-amber-200 leading-relaxed flex items-start gap-1.5" data-testid="metab-lifestyle-disclaimer">
                            <span className="shrink-0">⚠️</span>
                            <span>{lang === "bg" ? p.metabolites.disclaimer_bg : p.metabolites.disclaimer_en}</span>
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <h4 className="text-xs font-bold opacity-70 mb-1">{t.downstream}</h4>
                      <ul className="space-y-1 text-xs">
                        {p.downstream_bg.map((d, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-pink-500 mt-0.5">→</span>
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-xs font-bold opacity-70 mb-2">{t.levers}</h4>
                      <ul className="space-y-2">
                        {p.modifiable.map((m, i) => (
                          <li key={i} className="flex items-start justify-between gap-2 text-xs" data-testid={`pathway-${p.id}-lever-${i}`}>
                            <span className="flex-1 leading-snug">{m.action}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0 ${EVIDENCE_COLORS[m.evidence] || EVIDENCE_COLORS.C}`}>
                              {m.evidence}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}

      <div className="mt-3 p-3 rounded-lg bg-amber-500/10 text-[11px] leading-snug flex items-start gap-2" data-testid="molecular-disclaimer">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-70" />
        <span>{molecular?.disclaimer}</span>
      </div>
    </div>
  );
}
